import { FC } from "react";
import dayjs from "dayjs";

import {
  evaluateCategoriesJa,
  evaluateCategoryKeys,
  IEvaluateCategoryKeys,
} from "../../../const";
import { arraySum, maxIndex, minIndex } from "../../../utils/array";
import { useSummaryData } from "../../../repository/useSummary";
import { useBranchData } from "../../../repository/branch/get";
import useBID from "../../../utils/useBid";
import useTimelineData from "../../../repository/useTimelineData";
import Skeleton from "components/atom/Skeleton";

type SummaryCardTitleProps = {
  branchName: string | undefined;
};
const SummaryCardTitle: FC<SummaryCardTitleProps> = ({ branchName }) => (
  <p className="is-size-4 is-size-5-mobile">{branchName || <Skeleton />}</p>
);

type SummaryCardBestWorstProps = {
  bestCategory: string;
  worstCategory: string;
  starThisMonth: number | null;
  starLastMonth: number | null;
};
/**
 * 強みと伸びしろ
 */
const SummaryCardBestWorst: FC<SummaryCardBestWorstProps> = ({
  bestCategory,
  worstCategory,
  starThisMonth,
  starLastMonth,
}) => {
  return (
    <>
      <p>強み: {bestCategory}</p>
      <p>伸びしろ: {worstCategory}</p>
      {starThisMonth && starLastMonth && (
        <p>
          ★ {starThisMonth.toFixed(2)} (先月比{" "}
          {starLastMonth - starThisMonth > 0 ? "+" : ""}{" "}
          {(starLastMonth - starThisMonth).toFixed(2)}pt)
        </p>
      )}
    </>
  );
};

type SummaryCardNumLotteryAndQuestionnairesProps = {
  yesterdayNumStar: number;
  yesterdayNumQuestionnaires: number;
  lastweekNumStar: number;
  lastweekNumQuestionnaires: number;
};
/**
 * 昨日と一週間の回答数
 * @param param0
 * @returns
 */
const SummaryCardNumLotteryAndQuestionnaires: FC<
  SummaryCardNumLotteryAndQuestionnairesProps
> = ({
  yesterdayNumStar,
  yesterdayNumQuestionnaires: yesterdaynumQuestionnaires,
  lastweekNumStar,
  lastweekNumQuestionnaires,
}) => (
  <>
    <p>
      昨日の抽選数: {yesterdayNumStar}, アンケート回答数:{" "}
      {yesterdaynumQuestionnaires}
    </p>
    <p>
      一週間の抽選数: {lastweekNumStar}, アンケート回答数:{" "}
      {lastweekNumQuestionnaires}
    </p>
  </>
);

const SummaryCard: FC = () => {
  const bId = useBID();
  const { data: branch, isLoading: isBranchLoading } = useBranchData({ bId });
  const { data: timelineData, isLoading: isTLLoading } = useTimelineData({
    bId,
    startAt: dayjs().subtract(2, "month").toDate(),
    interval: "month",
    amount: 2,
  });
  const { data: summaryData, isLoading: isSummaryLoading } = useSummaryData({
    bId,
  });

  const scores = timelineData
    ? evaluateCategoryKeys.map((key) => {
        return (
          arraySum(timelineData[key as IEvaluateCategoryKeys].pos) -
          arraySum(timelineData[key as IEvaluateCategoryKeys].neg)
        );
      })
    : [];

  const bestCategory = evaluateCategoriesJa[maxIndex(scores)];
  const worstCategory = evaluateCategoriesJa[minIndex(scores)];
  const branchName = branch?.branchName;

  const starThisMonth = timelineData?.star.slice(-1)[0];
  const starLastMonth = timelineData?.star.slice(-2)[0];

  return (
    <div className="my-4 ">
      {isTLLoading || isBranchLoading || isSummaryLoading ? (
        <Skeleton className="card" height={300} />
      ) : (
        <div className="card is-size-5 is-size-6-mobile">
          <div className="card-content">
            <SummaryCardTitle branchName={branchName} />
            <div className="my-2">
              <SummaryCardBestWorst
                bestCategory={bestCategory}
                worstCategory={worstCategory}
                starLastMonth={starLastMonth ?? null}
                starThisMonth={starThisMonth ?? null}
              />
            </div>
            <div className="my-2">
              {summaryData ? (
                <SummaryCardNumLotteryAndQuestionnaires
                  yesterdayNumStar={summaryData.yesterday.numStar}
                  yesterdayNumQuestionnaires={
                    summaryData.yesterday.numQuestionnaires
                  }
                  lastweekNumQuestionnaires={
                    summaryData.lastweek.numQuestionnaires
                  }
                  lastweekNumStar={summaryData.lastweek.numStar}
                />
              ) : (
                <p>データの取得に失敗しました。</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SummaryCard;
